import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import { buildWeeklyReview } from "./utils/buildWeeklyReview";
import { sampleWeeklyReviews } from "./data/sampleWeeklyReviews";
import jsPDF from "jspdf";


const PDWeekly = ({useSample, isActiveSub, startDate, endDate}) => {
    const authState = useSelector((state) => state.authReducer);
    const [fetchData] = useFetch();
    const userId = authState?.user?._id;
    const userCode = authState?.user?.think5whyscode;
    const [loading, setLoading] = useState(false);
    const [selectedWeek, setSelectedWeek] = useState(0);
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; 

    const [rawData, setRawData] = useState({ todos: [], roachEntries: [] });

      const fetchWeeklyReview = async () => {
        try {
            setLoading(true);
            const config = {
                url: "/pd/my/todoroaches/raw",
                method: "post",
                data: {
                    userId,
                    startDate: startDate,
                    endDate: endDate,
                },
                headers: {
                    Authorization: authState.token,
                },
            };

            const response = await fetchData(config, { silent: true });
            if (response?.status) {
                const rawTodos = response?.todos || [];
                const rawRoach = response?.roachEntries || [];

                setRawData({ todos: rawTodos, roachEntries: rawRoach });
            }
        } catch (error) {
            console.error("Weekly Fetch Error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId && startDate && endDate) {
            fetchWeeklyReview();
            setSelectedWeek(0);
        }
    }, [userId, startDate, endDate]);

    // ====================== WEEK RANGES ======================
    const getWeekRanges = () => {
        if (!startDate || !endDate) return [];

        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        const totalDays = Math.ceil((end - start) / 86400000);
        const chunkSize = Math.max(1, Math.ceil(totalDays / 12));

        return Array.from({ length: 12 }, (_, i) => {
            const from = new Date(start);
            from.setDate(start.getDate() + i * chunkSize);

            const to = new Date(start);
            to.setDate(start.getDate() + (i + 1) * chunkSize);
            if (to > end) to.setTime(end.getTime());

            return {
                start: from.toISOString().split("T")[0],
                end: to.toISOString().split("T")[0],
            };
        });
    };

    const weekRanges = useMemo(() => getWeekRanges(), [startDate, endDate]);
    const activeWeekRange = weekRanges?.[selectedWeek] || null;

    // ====================== WEEKLY DATA FOR SELECTED WEEK ======================
    const weeklyDataForSelectedWeek = useMemo(() => {
        if (!rawData.todos.length || !activeWeekRange) return null;

        return buildWeeklyReview({
            todos: rawData.todos,
            roachEntries: rawData.roachEntries,
            weekStart: activeWeekRange.start,
            weekEnd: activeWeekRange.end,
        });
    }, [rawData, activeWeekRange]);

    // ====================== FINAL DATA FOR RENDER ======================
    const sampleWeekData = sampleWeeklyReviews.find(w => w.weekNumber === selectedWeek + 1) || null;
    // const d = weeklyDataForSelectedWeek || {
    const d = useSample ? sampleWeekData : weeklyDataForSelectedWeek || {
        weekNumber: selectedWeek + 1,
        score: 0,
        stats: { total: 0, done: 0, planned: 0, cancelled: 0 },
        trend: [0, 0, 0, 0, 0, 0, 0],
        bestDay: "-",
        weakDay: "-",
        objectives: {},
        roach: { avgScore: 0, type: "Unknown Survivor" },
        insights: [],
        signals: { focus: 0, consistency: 0, discipline: 0, resilience: 0 },
        weaknesses: [],
    };

    const objectivesColorMap = { 
        Learning: "bg-cyan-400", 
        Career: "bg-green-400", 
        Discipline: "bg-yellow-400", 
        Health: "bg-pink-400", 
        Build: "bg-purple-400", 
        Productivity: "bg-orange-400", 
        Focus: "bg-blue-400", 
        Growth: "bg-emerald-400", 
        Random: "bg-red-400", 
    }; 

    const getSignalLabel = (score = 0) => { 
        if (score >= 80) return "Elite"; 
        if (score >= 65) return "Strong"; 
        if (score >= 45) return "Average"; 
        if (score >= 25) return "Weak"; 
        return "Critical"; 
    };

    const handleShareWeeklyReview = () => {

        const pdf = new jsPDF("p", "mm", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        let y = 20;

        const addSection = (title) => {

            if (y > pageHeight - 25) {
                pdf.addPage();
                y = 20;
            }

            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(14);
            pdf.text(title, 15, y);

            y += 8;
        };

        const addText = (text) => {

            if (!text) return;

            const lines = pdf.splitTextToSize(
                String(text),
                180
            );

            if (y + lines.length * 5 > pageHeight - 20) {
                pdf.addPage();
                y = 20;
            }

            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(10);
            pdf.text(lines, 15, y);

            y += lines.length * 5 + 3;
        };

        // ==================================
        // COVER
        // ==================================

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(22);

        pdf.text(
            `PurpleDoor Weekly Review`,
            pageWidth / 2,
            25,
            { align: "center" }
        );

        pdf.setFontSize(11);

        pdf.text(`Week ${selectedWeek + 1}`, pageWidth / 2, 35, { align: "center" });

        pdf.text(
            `Generated: ${new Date().toLocaleString()}`,
            pageWidth / 2,
            42,
            { align: "center" }
        );

        y += 20;

        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(`Name: ${authState?.user?.name}`, 15, y);

        y += 10;

        // ==================================
        // OVERVIEW
        // ==================================

        addSection("Weekly Overview");

        addText(
            `Weekly Productivity Score: ${d?.score || 0}/100`
        );

        addText(
            "Track weekly execution consistency, productivity momentum and behavioral growth."
        );

        // ==================================
        // TASK SUMMARY
        // ==================================

        addSection("Task Statistics");

        addText(`Total Tasks: ${d?.stats?.total || 0}`);
        addText(`Completed Tasks: ${d?.stats?.done || 0}`);
        addText(`Planned Tasks: ${d?.stats?.planned || 0}`);
        addText(`Cancelled Tasks: ${d?.stats?.cancelled || 0}`);

        // ==================================
        // EXECUTION PATTERN
        // ==================================

        addSection("Execution Pattern");

        addText(
            `Best Day: ${d?.bestDay || "-"}`
        );

        addText(
            "Highest execution and productivity performance."
        );

        addText(
            `Weak Day: ${d?.weakDay || "-"}`
        );

        addText(
            "Lowest consistency and highest distraction risk."
        );

        // ==================================
        // DAILY TREND
        // ==================================

        addSection("Daily Trend Analysis");

        (d?.trend || []).forEach((value, index) => {

            addText(
                `${days[index]} : ${value}%`
            );

        });

        // ==================================
        // SIGNALS
        // ==================================

        addSection("Behavior Signals");

        addText(
            `Focus: ${d?.signals?.focus || 0}% (${getSignalLabel(
                d?.signals?.focus
            )})`
        );

        addText(
            `Consistency: ${d?.signals?.consistency || 0}% (${getSignalLabel(
                d?.signals?.consistency
            )})`
        );

        addText(
            `Discipline: ${d?.signals?.discipline || 0}% (${getSignalLabel(
                d?.signals?.discipline
            )})`
        );

        addText(
            `Resilience: ${d?.signals?.resilience || 0}% (${getSignalLabel(
                d?.signals?.resilience
            )})`
        );

        // ==================================
        // OBJECTIVES
        // ==================================

        addSection("Objective Breakdown");

        Object.entries(
            d?.objectives || {}
        ).forEach(([key, value]) => {

            const percentage = Math.round(
                (value /
                    Math.max(
                        d?.stats?.total || 1,
                        1
                    )) * 100
            );

            addText(
                `${key}: ${value} activities (${percentage}%)`
            );

        });

        // ==================================
        // SURVIVOR PROFILE
        // ==================================

        addSection("Survivor Profile");

        addText(
            `Profile Type: ${
                d?.roach?.type ||
                "Unknown Survivor"
            }`
        );

        addText(
            `Average Survivor Score: ${
                d?.roach?.avgScore || 0
            }/9`
        );

        addText(
            "Behavioral resilience pattern derived from daily survival assessments."
        );

        // ==================================
        // WEAK AREAS
        // ==================================

        addSection("Weak Areas");

        if (d?.weaknesses?.length) {

            d.weaknesses.forEach((item) => {

                addText(`• ${item}`);

            });

        } else {

            addText(
                "No major weakness identified."
            );

        }

        // ==================================
        // EXECUTION RATIOS
        // ==================================

        addSection("Execution Ratios");

        const completionRate = Math.round(
            ((d?.stats?.done || 0) /
                Math.max(
                    d?.stats?.total || 1,
                    1
                )) * 100
        );

        const planningRate = Math.round(
            ((d?.stats?.planned || 0) /
                Math.max(
                    d?.stats?.total || 1,
                    1
                )) * 100
        );

        const dropRate = Math.round(
            ((d?.stats?.cancelled || 0) /
                Math.max(
                    d?.stats?.total || 1,
                    1
                )) * 100
        );

        addText(
            `Completion Ratio: ${completionRate}%`
        );

        addText(
            `Planning Ratio: ${planningRate}%`
        );

        addText(
            `Drop Ratio: ${dropRate}%`
        );

        // ==================================
        // INSIGHTS
        // ==================================

        addSection("Weekly Insights");

        if (d?.insights?.length) {

            d.insights.forEach((item) => {

                addText(`• ${item}`);

            });

        } else {

            addText(
                "No weekly insights generated."
            );

        }

        // ==================================
        // FINAL SUMMARY
        // ==================================

        addSection("Weekly Outcome Summary");

        addText(
            `Overall Weekly Score: ${d?.score || 0}/100`
        );

        addText(
            `Best Performing Day: ${
                d?.bestDay || "-"
            }`
        );

        addText(
            `Primary Survivor Type: ${
                d?.roach?.type ||
                "Unknown Survivor"
            }`
        );

        addText(
            "Generated by Think5Whys PurpleDoor Weekly Behaviour Review Engine."
        );

        pdf.save(
            `PurpleDoor-Weekly-Review-Week-${
                d?.weekNumber || selectedWeek + 1
            }.pdf`
        );
    };

    return (
        <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-green-400 mt-2">
            {useSample && (
                <div className="space-y-6 bg-gray-700 p-6 rounded-xl text-white border-t-2 border-blue-600 mt-2">
                    📦 You are viewing SAMPLE DASHBOARD DATA (for demo purpose)
                </div>
            )}
            {/* WEEK SELECTOR */}
            <div className="flex gap-2 overflow-x-auto mt-4 pb-1">
                {[...Array(12)].map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setSelectedWeek(i)}
                        className={`px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                            selectedWeek === i
                                ? "bg-cyan-500 text-black"
                                : "bg-[#111827] text-gray-400"
                        }`}
                    >
                        Week {i + 1}
                    </button>
                ))}
            </div>

            {/* HEADER */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">

                <div className="flex items-start justify-between">

                    <div>

                        <div className="text-lg font-bold">
                            📊 Weekly Review
                        </div>

                        <div className="text-xs text-gray-300 mt-1">
                            Track your weekly consistency & productivity
                        </div>

                    </div>

                    <div className="bg-cyan-500/20 text-cyan-300 px-3 py-2 rounded-xl text-sm font-bold">
                        Week {selectedWeek + 1}
                    </div>

                </div>

                <div className="mt-5">

                    <div className="flex items-end gap-2">

                        <div className="text-4xl font-black text-white">
                            {d?.score || 0}
                        </div>

                        <div className="text-sm text-gray-400 pb-1">
                            /100 Productivity
                        </div>

                    </div>

                    <div className="w-full h-3 bg-gray-800 rounded-full overflow-hidden mt-4">

                        <div
                            className="h-full bg-cyan-400 rounded-full transition-all"
                            style={{
                                width: `${d?.score || 0}%`,
                            }}
                        />

                    </div>

                    <div className="flex justify-between mt-2 text-[10px] text-gray-500">
                        <span>Low Momentum</span>
                        <span>High Momentum</span>
                    </div>

                </div>

            </div>

            {/* TASK STATS */}
            <div className="grid grid-cols-4 gap-2">

                <div className="bg-[#1f2937] rounded-xl p-3 border border-gray-800">

                    <div className="text-[10px] text-gray-400">
                        📋 Total
                    </div>

                    <div className="text-lg font-black mt-1">
                        {d?.stats?.total || 0}
                    </div>

                </div>

                <div className="bg-[#1f2937] rounded-xl p-3 border border-gray-800">

                    <div className="text-[10px] text-gray-400">
                        ✅ Done
                    </div>

                    <div className="text-lg font-black text-green-300 mt-1">
                        {d?.stats?.done || 0}
                    </div>

                </div>

                <div className="bg-[#1f2937] rounded-xl p-3 border border-gray-800">

                    <div className="text-[10px] text-gray-400">
                        ⏳ Planned
                    </div>

                    <div className="text-lg font-black text-yellow-300 mt-1">
                        {d?.stats?.planned || 0}
                    </div>

                </div>

                <div className="bg-[#1f2937] rounded-xl p-3 border border-gray-800">

                    <div className="text-[10px] text-gray-400">
                        ❌ Cancelled
                    </div>

                    <div className="text-lg font-black text-red-300 mt-1">
                        {d?.stats?.cancelled || 0}
                    </div>

                </div>

            </div>

            {/* BEST DAY + WEAK DAY */}
            <div className="grid grid-cols-2 gap-2">

                <div className="bg-[#1f2937] rounded-xl p-4 border border-gray-800">

                    <div className="text-xs text-gray-400">
                        🔥 Best Day
                    </div>

                    <div className="text-xl font-black mt-2 text-green-300">
                        {d?.bestDay || "-"}
                    </div>

                    <div className="text-[11px] text-gray-500 mt-1">
                        Peak execution detected
                    </div>

                </div>

                <div className="bg-[#1f2937] rounded-xl p-4 border border-gray-800">

                    <div className="text-xs text-gray-400">
                        ⚠️ Weak Day
                    </div>

                    <div className="text-xl font-black mt-2 text-red-300">
                        {d?.weakDay || "-"}
                    </div>

                    <div className="text-[11px] text-gray-500 mt-1">
                        Distraction vulnerability
                    </div>

                </div>

            </div>

            {/* DAILY TREND */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">

                <div className="flex items-center justify-between mb-4">

                    <div className="text-sm font-bold">
                        📈 Daily Trend
                    </div>

                    <div className="text-[10px] text-gray-400">
                        Mon → Sun
                    </div>

                </div>

                <div className="space-y-3">

                    {(d?.trend || []).map((v, i) => (
                        <div
                            key={i}
                            className="flex items-center gap-2"
                        >

                            <div className="w-10 text-xs text-gray-300">
                                {days[i]}
                            </div>

                            <div className="flex-1 h-3 bg-gray-800 rounded-full overflow-hidden">

                                <div
                                    className="h-full bg-cyan-400 rounded-full transition-all"
                                    style={{
                                        width: `${v}%`,
                                    }}
                                />

                            </div>

                            <div className="w-10 text-right text-xs font-bold text-white">
                                {v}
                            </div>

                        </div>
                    ))}

                </div>

            </div>

            {/* SIGNALS */}
            <div className="grid grid-cols-2 gap-2">

                <div className="bg-[#1f2937] rounded-xl p-4 border border-gray-800">

                    <div className="text-xs text-gray-400">
                        🎯 Focus
                    </div>

                    <div className="text-2xl font-black text-cyan-300 mt-2">
                        {d?.signals?.focus || 0}%
                    </div>

                    <div className="text-[11px] text-gray-500 mt-1">
                        {getSignalLabel(d?.signals?.focus)}
                    </div>

                </div>

                <div className="bg-[#1f2937] rounded-xl p-4 border border-gray-800">

                    <div className="text-xs text-gray-400">
                        ⚡ Consistency
                    </div>

                    <div className="text-2xl font-black text-green-300 mt-2">
                        {d?.signals?.consistency || 0}%
                    </div>

                    <div className="text-[11px] text-gray-500 mt-1">
                        {getSignalLabel(d?.signals?.consistency)}
                    </div>

                </div>

                <div className="bg-[#1f2937] rounded-xl p-4 border border-gray-800">

                    <div className="text-xs text-gray-400">
                        🧠 Discipline
                    </div>

                    <div className="text-2xl font-black text-yellow-300 mt-2">
                        {d?.signals?.discipline || 0}%
                    </div>

                    <div className="text-[11px] text-gray-500 mt-1">
                        {getSignalLabel(d?.signals?.discipline)}
                    </div>

                </div>

                <div className="bg-[#1f2937] rounded-xl p-4 border border-gray-800">

                    <div className="text-xs text-gray-400">
                        🪳 Resilience
                    </div>

                    <div className="text-2xl font-black text-pink-300 mt-2">
                        {d?.signals?.resilience || 0}%
                    </div>

                    <div className="text-[11px] text-gray-500 mt-1">
                        {getSignalLabel(d?.signals?.resilience)}
                    </div>

                </div>

            </div>

            {/* OBJECTIVE BREAKDOWN */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">

                <div className="flex items-center justify-between mb-4">

                    <div className="text-sm font-bold">
                        🎯 Objective Breakdown
                    </div>

                    <div className="text-[10px] text-gray-400">
                        Weekly priorities
                    </div>

                </div>

                <div className="space-y-3">

                    {Object.entries(d?.objectives || {}).map(([key, value]) => {

                        const percentage = Math.round(
                            (value / Math.max(d?.stats?.total || 1, 1)) * 100
                        );

                        return (
                            <div key={key}>

                                <div className="flex items-center justify-between mb-1">

                                    <div className="text-xs text-gray-300">
                                        {key}
                                    </div>

                                    <div className="text-xs text-white font-bold">
                                        {value}
                                    </div>

                                </div>

                                <div className="w-full h-3 bg-gray-800 rounded-full overflow-hidden">

                                    <div
                                        className={`h-full rounded-full ${
                                            objectivesColorMap[key] || "bg-cyan-400"
                                        }`}
                                        style={{
                                            width: `${percentage}%`,
                                        }}
                                    />

                                </div>

                            </div>
                        );
                    })}

                </div>

            </div>

            {/* ROACH PROFILE */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">

                <div className="flex items-center justify-between">

                    <div className="text-sm font-bold">
                        🪳 Survivor Profile
                    </div>

                    <div className="text-xs text-pink-300 font-bold">
                        {d?.roach?.avgScore || 0}/9
                    </div>

                </div>

                <div className="mt-3 text-2xl font-black text-pink-300">
                    {d?.roach?.type || "Unknown Survivor"}
                </div>

                <div className="text-xs text-gray-400 mt-2">
                    Behavioral resilience pattern derived from your daily survival assessments.
                </div>

            </div>

            {/* WEAK AREAS */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">

                <div className="flex items-center justify-between">

                    <div className="text-sm font-bold">
                        ⚠️ Weak Areas
                    </div>

                    <div className="text-[10px] text-red-300">
                        Needs improvement
                    </div>

                </div>

                <div className="flex flex-wrap gap-2 mt-4">

                    {(d?.weaknesses || []).map((w, i) => (
                        <span
                            key={i}
                            className="bg-red-500/20 text-red-300 px-3 py-2 rounded-lg text-xs border border-red-500/20"
                        >
                            {w}
                        </span>
                    ))}

                </div>

            </div>

            {/* EXECUTION RATIO */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">

                <div className="text-sm font-bold mb-4">
                    ⚡ Execution Ratio
                </div>

                <div className="grid grid-cols-3 gap-3">

                    <div className="bg-[#111827] rounded-xl p-3">
                        <div className="text-[10px] text-gray-400">
                            Completion
                        </div>

                        <div className="text-xl font-black text-green-300 mt-1">
                            {Math.round(
                                ((d?.stats?.done || 0) /
                                Math.max(d?.stats?.total || 1, 1)) * 100
                            )}%
                        </div>
                    </div>

                    <div className="bg-[#111827] rounded-xl p-3">
                        <div className="text-[10px] text-gray-400">
                            Planning
                        </div>

                        <div className="text-xl font-black text-yellow-300 mt-1">
                            {Math.round(
                                ((d?.stats?.planned || 0) /
                                Math.max(d?.stats?.total || 1, 1)) * 100
                            )}%
                        </div>
                    </div>

                    <div className="bg-[#111827] rounded-xl p-3">
                        <div className="text-[10px] text-gray-400">
                            Drop Rate
                        </div>

                        <div className="text-xl font-black text-red-300 mt-1">
                            {Math.round(
                                ((d?.stats?.cancelled || 0) /
                                Math.max(d?.stats?.total || 1, 1)) * 100
                            )}%
                        </div>
                    </div>

                </div>

            </div>

            {/* INSIGHTS */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">

                <div className="text-sm font-bold mb-4">
                    🧠 Weekly Insight
                </div>

                <div className="space-y-2">

                    {(d?.insights || []).map((insight, i) => (
                        <div
                            key={i}
                            className="bg-[#111827] rounded-xl p-3"
                        >

                            <div className="text-xs text-cyan-400 font-bold">
                                Insight {i + 1}
                            </div>

                            <div className="text-xs text-gray-300 mt-1">
                                {insight}
                            </div>

                        </div>
                    ))}

                </div>

            </div>

            <div className="flex flex-row gap-2">
                <button className="w-48 mt-4 bg-cyan-500 hover:bg-cyan-400 text-black py-1 rounded-xl text-xs transition-all">
                    🎯 Continue Weekly Progress
                </button>
                <button
                    onClick={handleShareWeeklyReview}
                    className="w-48 bg-green-600 hover:bg-green-500 transition-all py-1 rounded-xl text-xs"
                >
                    📄 Share Weekly Review 
                </button>
            </div>
        

            {/* LOADING */}
            {loading && (
                <div className="text-center text-xs text-gray-500 py-2">
                    Loading weekly review...
                </div>
            )}

        </div>
    );
};

export default PDWeekly;



